import { useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useLoaderData, useLocation, useRouteError, useRouteLoaderData } from 'react-router';
import FingerprintSDK from '@cx-sdk/fp';
import RootErrorPage, { DEFAULT_RESOURCES_URL, extractException } from '@cx-web/components/composites/RootErrorPage';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import { BetslipProvider } from '@cx-web/feature-betslip/context';
import { FeatureFlagProvider, featureFlags } from '@cx-web/feature-flags/provider';
import { GeolocationProvider } from '@cx-web/feature-geolocation/context';
import { LabelProvider } from '@cx-web/feature-i18n';
import { MyOCUserProvider } from '@cx-web/feature-myoc';
import { RepubProvider } from '@cx-web/feature-repub';
import { SiteConfigProvider } from '@cx-web/feature-site-config';
import { getRepubFromURL, isErrorWithStatus } from '@cx-web/utils';
import { getClientI18n } from '@cx-web/utils/getClientI18n';
import { captureException } from '@sentry/react-router';

import { PlusConfigProvider } from '~/providers/plus-config-context';

import { sendGzScrollMessage } from './gz/scripts';
import Body from './body';
import Head from './head';
import { type loader } from './root-loader';

export { loader } from './root-loader';

/**
 * Shared root error boundary. It must never fail: it reads only the lightweight
 * `'root'` loader (repub from the OC-Repub header, resourcesUrl from env) and falls
 * back to the URL-derived repub and the CDN default when that data is absent — which
 * is exactly when an API caused the error. RootErrorPage renders the whole document
 * from those inputs alone.
 */
export function ErrorBoundary() {
  const error = useRouteError();
  captureException(extractException(error));

  const rootLoaderData = useRouteLoaderData<typeof loader>('root');
  const location = useLocation();
  const repub = rootLoaderData?.repub ?? getRepubFromURL(new URL(`https://www.oddschecker.com${location.pathname}`));
  const errorStatus = isErrorWithStatus(error) ? error.status : null;

  return (
    <RootErrorPage
      errorStatus={errorStatus}
      repub={repub}
      resourcesUrl={rootLoaderData?.resourcesUrl ?? DEFAULT_RESOURCES_URL}
    />
  );
}

function App() {
  const {
    ENV,
    featureToggleDefinitions,
    geolocationInfo,
    labels,
    lng,
    myocData,
    oddsFormat,
    persistedBetslipData,
    repub,
    resources,
    siteConfig,
  } = useLoaderData<typeof loader>();

  const i18n = getClientI18n({ lng, resources });

  useEffect(() => {
    const fingerprintingEnabled = featureToggleDefinitions.find(
      def => def.name === featureFlags.SUBS__FINGERPRINTING,
    )?.enabled;
    if (!fingerprintingEnabled) {
      return;
    }

    async function fingerprintClient() {
      if (localStorage && !localStorage.getItem('fpsdk-set')) {
        try {
          const fingerprintSDK = await FingerprintSDK.load();
          const result = await fingerprintSDK.get();
          const response = await fetch('/api/cx/fp', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(result),
          });
          const payload = (await response.json()) as { visitorId: string };
          if (payload.visitorId) {
            localStorage.setItem('fpsdk-set', payload.visitorId);
          }
        } catch (error) {}
      }
    }
    fingerprintClient();
  }, [featureToggleDefinitions]);

  return (
    <I18nextProvider i18n={i18n}>
      <html lang={lng}>
        <FeatureFlagProvider featureToggleDefinitions={featureToggleDefinitions}>
          <RepubProvider repub={repub}>
            <PlusConfigProvider>
              <MyOCUserProvider myocData={myocData as MyOCData}>
                <GeolocationProvider countryCode={geolocationInfo.countryCode} state={geolocationInfo.subdivisionCode}>
                  <SiteConfigProvider siteConfig={siteConfig}>
                    <LabelProvider devEnv={ENV.NODE_ENV !== 'production'} labels={labels}>
                      <BetslipProvider
                        onBetslipOpenChange={isOpen => sendGzScrollMessage({ variant: 'betslip', isOpen })}
                        persistedBetslipData={persistedBetslipData}
                        userOddsFormat={oddsFormat}
                      >
                        <Head />
                        <Body />
                      </BetslipProvider>
                    </LabelProvider>
                  </SiteConfigProvider>
                </GeolocationProvider>
              </MyOCUserProvider>
            </PlusConfigProvider>
          </RepubProvider>
        </FeatureFlagProvider>
      </html>
    </I18nextProvider>
  );
}

export default App;
